// Ancoragem de overlay a um elemento real da tela: resolve o alvo por seletor,
// mede o rect, reage a resize/scroll e portaliza o nó para o `body`.
//
// Extraído de `@modules/global/components/tour/tour.vue` (modo ancorado) para
// ser compartilhado com o tour guiado da Jornada. Os nomes de data/método são
// os originais — `tour.unit.js` os afirma diretamente, e ele é o gate desta
// extração: a suíte dele passa sem uma edição.
//
// Contrato do consumidor (dois computeds):
//   anchorSelector() -> String   '' desliga o modo ancorado (nada é portalizado)
//   anchorActive()   -> Boolean  false suspende medição e listeners
//
// Opcional: `anchorDevePortalizar()`. O default deriva de `anchorSelector`, que
// serve a quem monta um overlay por passo (o Tour legado). Um host único e
// permanente monta com o tour ainda inativo — sem alvo, portanto — e precisa
// sobrescrever para `true`, senão nunca é portalizado e herda o stacking
// context do layout.
//
// O consumidor recebe `targetRect` (rect de viewport, ou null) e `targetEl` (o
// nó resolvido, ou null). `targetEl` não existia no tour original — o tour
// guiado precisa dele para detectar alvo dentro de modal e para ouvir o clique
// real sobre o elemento.

// Reagendamento do rect quando o alvo ainda não está no DOM. Em navegação
// client-side, cabeçalhos teleportados / conteúdo atrás de v-if async podem
// montar depois do auto-start — sem retry o overlay cairia no card centralizado
// (bug visto ao entrar em telas como Ligações vindo de outra página; F5 monta
// tudo a tempo e não reproduz). Janela curta e limitada; para ao encontrar.
const RECT_RETRY_INTERVAL_MS = 120
const RECT_RETRY_MAX_ATTEMPTS = 20

export const anchoredTargetMixin = {
  data() {
    return {
      // Rect (viewport) do alvo. null → sem alvo encontrado.
      targetRect: null,
      // Nó resolvido. Acompanha targetRect; null quando o alvo não existe.
      targetEl: null,
      onViewportChange: null,
      // Retry do alvo ausente (ver constantes no topo).
      rectRetryTimer: null,
      rectRetriesLeft: 0,
      // Listeners de viewport só existem enquanto a ancoragem está ativa —
      // evita resize/scroll ociosos nos passos inativos e quando não há
      // overlay aberto.
      viewportBound: false,
    }
  },

  computed: {
    // Sobrescrevível pelo consumidor (computed do componente vence o do mixin).
    anchorDevePortalizar() {
      return !!this.anchorSelector
    },
  },

  watch: {
    anchorActive(ativo) {
      if (ativo) {
        this.addViewportListeners()
        this.scheduleRectUpdate()
        return
      }
      this.removeViewportListeners()
      this.clearRectRetry()
      this.targetRect = null
      this.targetEl = null
    },

    anchorSelector() {
      if (this.anchorActive) this.scheduleRectUpdate()
    },
  },

  mounted() {
    if (!this.anchorDevePortalizar) return
    // Portaliza o overlay ancorado para o body: shells legados podem criar um
    // containing block (transform/filter/contain) num ancestral, deslocando o
    // spotlight position:fixed dos rects de viewport. No body ele fica imune.
    if (this.$el && document.body) {
      document.body.appendChild(this.$el)
    }
    this.onViewportChange = () => this.updateRect()
    if (this.anchorActive) {
      this.addViewportListeners()
      this.scheduleRectUpdate()
    }
  },

  beforeDestroy() {
    this.clearRectRetry()
    this.removeViewportListeners()
    this.onViewportChange = null
    // Remove o nó portalizado do body — Vue removeria do parent original,
    // que não é mais o dono após o appendChild.
    if (this.anchorDevePortalizar && this.$el && this.$el.parentNode) {
      this.$el.remove()
    }
  },

  methods: {
    // Idempotentes: registram/removem os listeners de viewport só uma vez,
    // guardados pela flag viewportBound. onViewportChange é a mesma referência
    // usada em add e remove.
    addViewportListeners() {
      if (this.viewportBound || !this.onViewportChange) return
      window.addEventListener('resize', this.onViewportChange)
      window.addEventListener('scroll', this.onViewportChange, true)
      this.viewportBound = true
    },

    removeViewportListeners() {
      if (!this.viewportBound || !this.onViewportChange) return
      window.removeEventListener('resize', this.onViewportChange)
      window.removeEventListener('scroll', this.onViewportChange, true)
      this.viewportBound = false
    },

    scheduleRectUpdate() {
      // Reabre a janela de retry a cada (re)agendamento — novo passo, reativação
      // ou mount — para tolerar alvos que ainda vão montar.
      this.rectRetriesLeft = RECT_RETRY_MAX_ATTEMPTS
      this.$nextTick(() => this.updateRect())
    },

    updateRect() {
      if (!this.anchorActive) {
        this.limparAlvo()
        this.clearRectRetry()
        return
      }
      const el = document.querySelector(this.anchorSelector)
      if (!el) {
        this.limparAlvo()
        this.scheduleRectRetry()
        return
      }
      this.clearRectRetry()
      this.medirAlvo(el)
    },

    limparAlvo() {
      this.targetRect = null
      this.targetEl = null
    },

    // Copia os seis campos em vez de guardar o DOMRect: o objeto nativo é
    // "vivo" em alguns browsers e a reatividade do Vue 2 não o acompanha.
    medirAlvo(el) {
      this.targetEl = el
      const r = el.getBoundingClientRect()
      this.targetRect = {
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        bottom: r.bottom,
        right: r.right,
      }
    },

    // Alvo ausente: tenta de novo por uma janela curta e limitada. Enquanto isso
    // o card fica centralizado (fallback), migrando para ancorado assim que o
    // alvo aparece.
    scheduleRectRetry() {
      if (this.rectRetryTimer || this.rectRetriesLeft <= 0) return
      this.rectRetriesLeft -= 1
      this.rectRetryTimer = setTimeout(() => {
        this.rectRetryTimer = null
        this.updateRect()
      }, RECT_RETRY_INTERVAL_MS)
    },

    clearRectRetry() {
      if (this.rectRetryTimer) {
        clearTimeout(this.rectRetryTimer)
        this.rectRetryTimer = null
      }
    },
  },
}
